import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  QrCode, Calendar, Play, Camera, Video, Zap, 
  Users, MessageCircle, Briefcase, Globe, ArrowRight 
} from 'lucide-react';

const AllAppsLanding = () => {
  const navigate = useNavigate();

  const apps = [
    {
      icon: <QrCode className="w-12 h-12" />,
      name: "DigiConnect",
      tagline: "Digital Business Cards",
      description: "Create smart business cards with QR codes and lead capture. No login required for visitors.",
      gradient: "from-slate-900 to-slate-700",
      demoLink: "/profile/demo-user-12345",
      learnMore: "/landing/digi-connect",
      badge: "Popular"
    },
    {
      icon: <Calendar className="w-12 h-12" />,
      name: "DigiCalendar",
      tagline: "Meeting Scheduler",
      description: "Google Calendar sync, public booking pages, timezone support. Clients book without accounts.",
      gradient: "from-green-600 to-green-800",
      demoLink: "/schedule/demo-user-12345",
      learnMore: "/landing/digi-calendar"
    },
    {
      icon: <Play className="w-12 h-12" />,
      name: "Community Videos",
      tagline: "Short Video Sharing",
      description: "Social-style feed with 90-second videos, likes, views, and auto-play. Share and discover content.",
      gradient: "from-pink-600 to-rose-600",
      demoLink: "/community-videos",
      learnMore: "/landing/digi-community",
      badge: "New"
    },
    {
      icon: <Camera className="w-12 h-12" />,
      name: "Media Events",
      tagline: "Photo/Video Sharing",
      description: "Create events with QR codes. Anyone can upload photos/videos without login. Perfect for weddings.",
      gradient: "from-cyan-600 to-blue-600",
      demoLink: "/media-event/9abf261a-ec18-47ed-afc4-dd8259b19e72",
      learnMore: "/photo-video-events",
      badge: "New"
    },
    {
      icon: <Video className="w-12 h-12" />,
      name: "DigiMeet",
      tagline: "Video Conferencing",
      description: "Professional video calls with screen sharing, chat, and transcription. Host meetings easily.",
      gradient: "from-blue-600 to-blue-800",
      demoLink: "/digi-meet",
      learnMore: "/landing/digi-meet"
    },
    {
      icon: <Zap className="w-12 h-12" />,
      name: "Digi Squad",
      tagline: "Team Collaboration",
      description: "Build squad profiles, manage teams, collaborate on projects. Showcase your team's expertise.",
      gradient: "from-amber-600 to-orange-600",
      demoLink: "/digi-squad",
      learnMore: "/digi-squad",
      badge: "New"
    },
    {
      icon: <Briefcase className="w-12 h-12" />,
      name: "Digi CRM",
      tagline: "Customer Management",
      description: "Track leads, manage contacts, follow-up reminders. Grow your business with smart CRM.",
      gradient: "from-purple-600 to-purple-800",
      demoLink: "/digi-crm",
      learnMore: "/landing/digi-crm"
    },
    {
      icon: <Globe className="w-12 h-12" />,
      name: "Website Builder",
      tagline: "AI-Powered Sites",
      description: "Create professional websites in minutes. AI-generated content, custom domains, mobile-responsive.",
      gradient: "from-indigo-600 to-purple-600",
      demoLink: "/auto-website-builder",
      learnMore: "/landing/digi-website"
    },
    {
      icon: <MessageCircle className="w-12 h-12" />,
      name: "Digi Presentor",
      tagline: "Pitch Creator",
      description: "AI-powered networking pitches. Create compelling presentations for events and meetings.",
      gradient: "from-teal-600 to-cyan-600",
      demoLink: "/presentor",
      learnMore: "/landing/digi-presentor"
    },
    {
      icon: <Users className="w-12 h-12" />,
      name: "Contact Groups",
      tagline: "Shared Contacts",
      description: "Organize contacts into groups, share via QR codes. Perfect for teams and events.",
      gradient: "from-emerald-600 to-green-600",
      demoLink: "/group/demo-group-12345",
      learnMore: "/landing/digi-connect"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            All <span className="text-blue-600">DigiDream</span> Apps
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
            Your Complete Digital Business Ecosystem - Try Every App Without Login
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full"
            >
              Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="px-8 py-6 text-lg rounded-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              View Pricing
            </Button>
          </div>
        </div>

        {/* Apps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {apps.map((app, index) => (
            <Card
              key={index}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-blue-300 relative"
            >
              {app.badge && (
                <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold text-white ${
                  app.badge === 'New' ? 'bg-gradient-to-r from-pink-500 to-rose-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'
                }`}>
                  {app.badge}
                </div>
              )}
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${app.gradient} flex items-center justify-center mb-4 text-white`}>
                {app.icon}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{app.name}</h3>
              <p className="text-sm text-blue-600 font-semibold mb-3">{app.tagline}</p>
              <p className="text-gray-600 mb-6 min-h-[60px]">{app.description}</p>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate(app.demoLink)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  Try Demo
                </Button>
                <Button
                  onClick={() => navigate(app.learnMore)}
                  variant="outline"
                  className="flex-1 border-2 border-gray-300 hover:border-blue-600 rounded-lg"
                >
                  Learn More
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white mt-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            All apps included - Start building your digital presence today
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full font-semibold"
            >
              Sign Up Free
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-white/20 hover:bg-white/30 text-white border-2 border-white px-8 py-6 text-lg rounded-full font-semibold"
            >
              View Plans & Pricing
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllAppsLanding;
